import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db, CAMPAIGN_ID } from '../../firebase';
import { useCampaign } from '../../contexts/CampaignContext';
import { useMap } from '../../contexts/MapContext';
import { OBJECT_TYPES } from '../../lib/objects';
import { NPC_TYPES } from '../../lib/npcs';
import { MONSTER_PACKS, THEMES } from '../../lib/monsterPacks';
import * as monsterPackRenderer from '../../lib/monsterPackRenderer';
import PlayerHistoryPanel from './PlayerHistoryPanel';

// Idle-frame thumbnail for a monster pack (first idle frame, down-facing).
function PackThumb({ packKey, size = 56 }) {
  const ref = useRef(null);
  const [, bump] = useState(0);
  useEffect(() => monsterPackRenderer.subscribe(() => bump((v) => v + 1)), []);
  useEffect(() => {
    const c = monsterPackRenderer.getMonsterPackFrame(packKey, 'idle', 'down', 0, size);
    if (!c || !ref.current) return;
    const dst = ref.current;
    dst.width = size; dst.height = size;
    const ctx = dst.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, size, size);
    ctx.drawImage(c, 0, 0);
  });
  return <canvas ref={ref} width={size} height={size} style={{ display: 'block', imageRendering: 'pixelated' }} />;
}

// ── Section header ──────────────────────────────────────────────────────────
function SectionHeader({ label, open, onToggle, badge }) {
  return (
    <button style={styles.sectionHeader} onClick={onToggle}>
      <span style={styles.sectionHeaderLabel}>
        {label}
        {badge ? <span style={styles.sectionBadge}>{badge}</span> : null}
      </span>
      <span style={styles.chevron}>{open ? '▲' : '▼'}</span>
    </button>
  );
}

// ── Players section / standalone panel ──────────────────────────────────────
// Exported so DMView can render it as its own sidebar tab.
export function PlayersSection({ open = true, getViewCenter }) {
  const { players, tokens, spawnToken, assignToken, removeToken, updateToken, deletePlayer } = useCampaign();
  const { spawnPoint, gridW, gridH } = useMap();
  const [spawning,    setSpawning]    = useState(null);
  const [placing,     setPlacing]     = useState(null);
  const [placingAll,  setPlacingAll]  = useState(false);
  const [confirming,  setConfirming]  = useState(null);
  const [historyFor,  setHistoryFor]  = useState(null);

  const playerList = Object.values(players);
  const tokenList  = Object.values(tokens).filter((t) => t.type === 'player');
  const waitingTokens = tokenList.filter((t) => t.hidden);
  // Player-type tokens with no matching player record — happens if the DM
  // deletes a player whose token had no ownerId yet, or after a stale write.
  // Surface them so the DM can clean up instead of being silently invisible.
  const orphanTokens = tokenList.filter((t) => !t.ownerId || !players[t.ownerId]);

  const findSpawnCell = (placedCoords = []) => {
    const sx = spawnPoint?.x ?? 1;
    const sy = spawnPoint?.y ?? 1;
    const occupied = new Set(
      Object.values(tokens)
        .filter((t) => !t.hidden && !t.defeated)
        .map((t) => `${t.x},${t.y}`)
    );
    for (const [px, py] of placedCoords) occupied.add(`${px},${py}`);
    for (let dy = 0; dy < 2; dy++) {
      for (let dx = 0; dx < 3; dx++) {
        const cx = Math.min(sx + dx, (gridW ?? 30) - 1);
        const cy = Math.min(sy + dy, (gridH ?? 20) - 1);
        if (!occupied.has(`${cx},${cy}`)) return { x: cx, y: cy };
      }
    }
    return { x: sx, y: sy };
  };

  const handleCreate = async (player) => {
    setSpawning(player.id);
    try {
      const existing = tokenList.find((t) => t.ownerId === player.id);
      if (existing) return;
      // Carry the player's character data forward so the new token uses
      // their paperdoll/style/stats — not the procedural "old style" sprite.
      const tokenId = await spawnToken({
        name:            player.charName || player.name,
        type:            'player',
        colour:          player.colour || playerColour(player.id),
        hp:              player.maxHp || 10,
        maxHp:           player.maxHp || 10,
        ac:              player.ac || 10,
        initiativeBonus: player.initiativeBonus || 0,
        speed:           player.speed || 30,
        visionRange:     player.visionRange ?? 60,
        class:           player.class || null,
        level:           player.level || 1,
        sizeCategory:    player.sizeCategory || 'medium',
        paperdoll:       player.paperdoll || null,
        combatStyle:     player.combatStyle || null,
        packKey:         player.packKey || null,
        ownerId:         player.id,
      });
      await assignToken(tokenId, player.id);
    } finally {
      setSpawning(null);
    }
  };

  const handlePlace = async (token) => {
    setPlacing(token.id);
    try {
      const { x, y } = findSpawnCell();
      await updateToken(token.id, { hidden: false, x, y });
    } finally {
      setPlacing(null);
    }
  };

  const handlePlaceAll = async () => {
    setPlacingAll(true);
    try {
      const placedCoords = [];
      for (const token of waitingTokens) {
        const { x, y } = findSpawnCell(placedCoords);
        placedCoords.push([x, y]);
        await updateToken(token.id, { hidden: false, x, y });
      }
    } finally {
      setPlacingAll(false);
    }
  };

  const handleDelete = async (player) => {
    // Pull tokens straight from Firestore — `tokenList` (React state) can lag the
    // most recent spawn, and the auto-spawn race in PlayerView occasionally creates
    // duplicates. Removing every match closes both orphan paths.
    const live = await getDocs(query(
      collection(db, 'campaigns', CAMPAIGN_ID, 'tokens'),
      where('ownerId', '==', player.id),
    ));
    await Promise.all(live.docs.map((d) => removeToken(d.id)));
    await deletePlayer(player.id);
    setConfirming(null);
  };

  const handleRemoveAllOrphans = async () => {
    await Promise.all(orphanTokens.map((t) => removeToken(t.id)));
  };

  if (!open) return null;

  return (
    <div style={styles.section}>
      {waitingTokens.length > 1 && (
        <button
          style={{ ...styles.placeAllBtn, ...(placingAll ? { opacity: 0.5 } : {}) }}
          onClick={handlePlaceAll}
          disabled={placingAll}
        >
          Place All ({waitingTokens.length})
        </button>
      )}
      {playerList.map((player) => {
        const token = tokenList.find((t) => t.ownerId === player.id);
        const isWaiting     = token?.hidden;
        const isReady       = token && !token.hidden;
        const isSpawning    = spawning === player.id;
        const isPlacing     = placing === token?.id;
        const isConfirming  = confirming === player.id;
        const hasChar       = !!player.charName;
        const isOnline      = player.connected && player.lastSeen && (Date.now() - player.lastSeen < 30000);
        const stats = [
          hasChar && (player.class ? `${player.class}${player.level ? ' ' + player.level : ''}` : null),
          hasChar && player.maxHp ? `${player.maxHp} HP` : null,
          hasChar && player.ac ? `AC ${player.ac}` : null,
          hasChar && player.sizeCategory && player.sizeCategory !== 'medium' ? player.sizeCategory : null,
          hasChar && player.speed ? `${player.speed}ft` : null,
        ].filter(Boolean);
        return (
          <div key={player.id} style={styles.playerRow}>
            <div style={styles.playerInfo}>
              <span style={{ ...styles.colourDot, background: player.colour || token?.colour || '#666' }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.playerNameLine}>
                  <span style={styles.playerName}>
                    {hasChar ? player.charName : <span style={styles.noCharName}>(no character)</span>}
                  </span>
                  <span
                    style={{ ...styles.onlineDot, background: isOnline ? '#5cb870' : '#4a3d28' }}
                    title={isOnline ? 'Online' : 'Offline'}
                  />
                </div>
                <span style={styles.playerMeta}>
                  login: {player.name}
                  {stats.length > 0 ? ' · ' + stats.join(' · ') : (hasChar ? '' : ' · no character set')}
                </span>
              </div>
            </div>
            <div style={styles.playerActions}>
              {!token && (
                <button
                  style={{ ...styles.spawnBtn, ...(isSpawning ? { opacity: 0.5 } : {}) }}
                  onClick={() => handleCreate(player)}
                  disabled={isSpawning}
                >
                  Create
                </button>
              )}
              {isWaiting && (
                <button
                  style={{ ...styles.spawnBtn, ...(isPlacing ? { opacity: 0.5 } : {}) }}
                  onClick={() => handlePlace(token)}
                  disabled={isPlacing}
                >
                  Place
                </button>
              )}
              {isWaiting && <span style={styles.waitingBadge}>Waiting</span>}
              {isReady && <span style={styles.assignedBadge}>Ready</span>}
              {!isConfirming ? (
                <>
                  <button
                    style={styles.deleteBtn}
                    onClick={() => setHistoryFor(player)}
                    title="View character snapshots — restore if accidentally edited or deleted"
                  >🔁</button>
                  <button style={styles.deleteBtn} onClick={() => setConfirming(player.id)} title="Delete account & token (snapshots persist; restore via 🔁)">🗑</button>
                </>
              ) : (
                <div style={styles.confirmRow}>
                  <span style={styles.confirmText}>
                    Delete {player.charName || player.name}? (snapshots persist via 🔁)
                  </span>
                  <button style={styles.confirmYes} onClick={() => handleDelete(player)}>Yes</button>
                  <button style={styles.confirmNo}  onClick={() => setConfirming(null)}>No</button>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {playerList.length === 0 && orphanTokens.length === 0 && (
        <div style={styles.emptyRosterMsg}>No players have joined yet.</div>
      )}

      {orphanTokens.length > 0 && (
        <div style={styles.orphanSection}>
          <div style={styles.orphanLabel}>
            Orphan tokens · {orphanTokens.length}
            <span style={styles.orphanLabelHint}>player record missing</span>
          </div>
          {orphanTokens.length > 1 && (
            <button style={styles.orphanCleanupBtn} onClick={handleRemoveAllOrphans}>
              Remove all orphans
            </button>
          )}
          {orphanTokens.map((t) => (
            <div key={t.id} style={styles.orphanRow}>
              <span style={{ ...styles.colourDot, background: t.colour || '#666' }} />
              <div style={styles.playerInfo}>
                <span style={styles.playerName}>{t.name || '(unnamed)'}</span>
                <span style={styles.playerMeta}>
                  {t.hidden ? 'hidden' : 'placed'} · id {t.id.slice(0, 6)}
                  {t.ownerId ? ` · owner ${t.ownerId.slice(0, 6)}` : ' · no owner'}
                </span>
              </div>
              <button style={styles.deleteBtn} onClick={() => removeToken(t.id)} title="Delete orphan token">🗑</button>
            </div>
          ))}
        </div>
      )}

      {historyFor && (
        <PlayerHistoryPanel player={historyFor} onClose={() => setHistoryFor(null)} />
      )}
    </div>
  );
}

function playerColour(id) {
  const colours = ['#c084fc','#60a5fa','#34d399','#fb923c','#f472b6','#facc15','#38bdf8','#a78bfa'];
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = id.charCodeAt(i) + ((hash << 5) - hash);
  return colours[Math.abs(hash) % colours.length];
}

// ── Monsters section (theme-browser only) ──────────────────────────────────
function MonstersSection({ open, getViewCenter }) {
  const { spawnToken } = useCampaign();
  const [spawning,    setSpawning]    = useState(null);
  const [spawnedMsg,  setSpawnedMsg]  = useState('');
  const [openThemes,  setOpenThemes]  = useState({});
  const [spawnSize,   setSpawnSize]   = useState(1);

  const toggleTheme = (t) => setOpenThemes((p) => ({ ...p, [t]: !p[t] }));

  const handleSpawnAll = async () => {
    setSpawning('__all__');
    try {
      const allKeys = Object.keys(MONSTER_PACKS);
      const cols = Math.ceil(Math.sqrt(allKeys.length));
      for (let i = 0; i < allKeys.length; i++) {
        const pk = allKeys[i];
        const pack = MONSTER_PACKS[pk];
        const gx = (i % cols) * 2;
        const gy = Math.floor(i / cols) * 2;
        await spawnToken({
          name: pack.name,
          type: 'monster',
          size: 1,
          hp: 10, maxHp: 10, ac: 12, cr: 1,
          colour: '#8b4a9a',
          packKey: pk,
          x: gx, y: gy,
        });
      }
      setSpawnedMsg(`Spawned all ${allKeys.length} packs in grid`);
      setTimeout(() => setSpawnedMsg(''), 4000);
    } finally {
      setSpawning(null);
    }
  };

  const handleSpawnPack = async (packKey) => {
    setSpawning(packKey);
    try {
      const pack = MONSTER_PACKS[packKey];
      const vc = getViewCenter?.() || { x: 0, y: 0 };
      const hpByScale = 10 * spawnSize * spawnSize;
      await spawnToken({
        name:   pack.name,
        type:   'monster',
        size:   spawnSize,
        hp: hpByScale, maxHp: hpByScale, ac: 12, cr: 1,
        colour: '#8b4a9a',
        packKey,
        x: vc.x, y: vc.y,
      });
      setSpawnedMsg(`${pack.name} (${spawnSize}×${spawnSize}) placed`);
      setTimeout(() => setSpawnedMsg(''), 2000);
    } finally {
      setSpawning(null);
    }
  };

  if (!open) return null;

  return (
    <>
      {spawnedMsg && <div style={{ ...styles.msg, margin: '8px 14px 0' }}>{spawnedMsg}</div>}

      {/* Debug: spawn every pack in a grid */}
      <div style={{ padding: '8px 14px' }}>
        <button
          style={{ ...styles.spawnBtn, width: '100%', background: '#6b21a8', padding: '6px 0' }}
          onClick={handleSpawnAll}
          disabled={!!spawning}
        >
          {spawning === '__all__' ? 'Spawning…' : `Spawn All ${Object.keys(MONSTER_PACKS).length} Packs (debug grid)`}
        </button>
      </div>

      {/* Shopkeeper presets — one-click NPC with shopId set */}
      <div style={{ padding: '6px 14px 0' }}>
        <div style={{ fontSize: 11, color: '#9a8b6e', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 4 }}>
          Shopkeepers
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {[
            { shopId: 'general',   icon: '🏪', label: 'General', name: 'Goods & Sundries' },
            { shopId: 'smithy',    icon: '⚒',  label: 'Smithy',  name: 'The Iron Forge' },
            { shopId: 'alchemist', icon: '🧪', label: 'Alchemist', name: "The Mixer's Stall" },
          ].map((s) => (
            <button
              key={s.shopId}
              style={{
                flex: 1,
                padding: '8px 0',
                background: '#0e1528',
                border: '1px solid #4a3d28',
                color: '#d4a847',
                fontFamily: "'Press Start 2P', monospace",
                fontWeight: 700,
                fontSize: 11,
                cursor: 'pointer',
                borderRadius: 4,
              }}
              onClick={async () => {
                const vc = getViewCenter?.() || { x: 0, y: 0 };
                await spawnToken({
                  name:   s.name,
                  type:   'npc',
                  size:   1,
                  hp: 10, maxHp: 10, ac: 10, cr: '0',
                  colour: '#a08020',
                  shopId: s.shopId,
                  x: vc.x, y: vc.y,
                });
                setSpawnedMsg(`${s.name} placed`);
                setTimeout(() => setSpawnedMsg(''), 2000);
              }}
            >
              {s.icon} {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Size picker — applied to the next pack the DM spawns */}
      <div style={styles.sizeRow}>
        <span style={styles.sizeLabel}>Size</span>
        {[1, 2, 3, 4].map((s) => (
          <button
            key={s}
            style={{
              ...styles.sizeBtn,
              ...(spawnSize === s ? styles.sizeBtnActive : {}),
            }}
            onClick={() => setSpawnSize(s)}
          >
            {s}×{s}
          </button>
        ))}
      </div>

      {Object.entries(THEMES).map(([theme, ids]) => (
        <div key={theme}>
          <button style={styles.groupBtn} onClick={() => toggleTheme(theme)}>
            <span>{theme} <span style={{ color: '#6a5d45', fontWeight: 400, marginLeft: 4 }}>({ids.length})</span></span>
            <span style={styles.chevron}>{openThemes[theme] ? '▲' : '▼'}</span>
          </button>
          {openThemes[theme] && (
            <div style={{ padding: '10px 14px', borderBottom: '1px solid #1a1a30' }}>
              <div style={styles.packGrid}>
                {ids.map((packKey) => {
                  const pack = MONSTER_PACKS[packKey];
                  if (!pack) return null;
                  return (
                    <button
                      key={packKey}
                      style={{ ...styles.packBtn, ...(spawning === packKey ? { opacity: 0.5 } : {}) }}
                      onClick={() => handleSpawnPack(packKey)}
                      disabled={!!spawning}
                      title={`Spawn ${pack.name} at ${spawnSize}×${spawnSize}`}
                    >
                      <PackThumb packKey={packKey} size={96} />
                      <span style={styles.packLabel}>{pack.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ))}
    </>
  );
}

// ── NPCs section (spawn palette only) ──────────────────────────────────────
function NpcsSection({ open, getViewCenter }) {
  const { spawnToken } = useCampaign();
  const [spawning,   setSpawning]   = useState(null);
  const [spawnedMsg, setSpawnedMsg] = useState('');
  const [customHp,   setCustomHp]   = useState({});

  const handleSpawn = async (npc) => {
    setSpawning(npc.id);
    try {
      const hp = parseInt(customHp[npc.id], 10) || npc.hp;
      const vc = getViewCenter?.() || { x: 0, y: 0 };
      await spawnToken({
        name:    npc.label,
        type:    'friendlyNPC',
        size:    1,
        hp,
        maxHp:   hp,
        ac:      npc.ac,
        speed:   npc.speed,
        colour:  npc.color,
        npcType: npc.id,
        x: vc.x, y: vc.y,
      });
      setSpawnedMsg(`${npc.icon} ${npc.label} placed`);
      setTimeout(() => setSpawnedMsg(''), 2000);
    } finally {
      setSpawning(null);
    }
  };

  if (!open) return null;

  return (
    <div style={styles.section}>
      {spawnedMsg && <div style={styles.msg}>{spawnedMsg}</div>}
      <div style={styles.objectGrid}>
        {NPC_TYPES.map((npc) => (
          <div key={npc.id} style={styles.objectEntry}>
            <button
              style={{ ...styles.objectBtn, background: npc.color, ...(spawning === npc.id ? { opacity: 0.5 } : {}) }}
              onClick={() => handleSpawn(npc)}
              disabled={!!spawning}
              title={`Place ${npc.label} (HP ${npc.hp}, AC ${npc.ac}, CR ${npc.cr})`}
            >
              <span style={styles.objIcon}>{npc.icon}</span>
              <span style={styles.objLabel}>{npc.label}</span>
              <span style={styles.objMeta}>CR {npc.cr}</span>
            </button>
            <div style={styles.hpRow}>
              <span style={styles.hpLabel}>HP</span>
              <input
                style={styles.hpInput}
                type="number" min="1"
                value={customHp[npc.id] ?? npc.hp}
                onChange={(e) => setCustomHp((p) => ({ ...p, [npc.id]: e.target.value }))}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Objects section ─────────────────────────────────────────────────────────
function ObjectsSection({ open, getViewCenter }) {
  const { spawnToken } = useCampaign();
  const [spawning,   setSpawning]   = useState(null);
  const [spawnedMsg, setSpawnedMsg] = useState('');
  const [customHp,   setCustomHp]   = useState({});

  const handleSpawn = async (obj) => {
    setSpawning(obj.id);
    try {
      const hp = parseInt(customHp[obj.id], 10) || obj.hp;
      const vc = getViewCenter?.() || { x: 0, y: 0 };
      await spawnToken({ name: obj.label, type: 'object', size: obj.size, ...(obj.sizeW ? { sizeW: obj.sizeW } : {}), ...(obj.sizeH ? { sizeH: obj.sizeH } : {}), hp, maxHp: hp, objectType: obj.id, colour: obj.color, passable: false, rotation: 0, x: vc.x, y: vc.y });
      setSpawnedMsg(`${obj.icon} ${obj.label} placed`);
      setTimeout(() => setSpawnedMsg(''), 2000);
    } finally {
      setSpawning(null);
    }
  };

  if (!open) return null;

  return (
    <div style={styles.section}>
      {spawnedMsg && <div style={styles.msg}>{spawnedMsg}</div>}
      <div style={styles.objectGrid}>
        {[...OBJECT_TYPES].sort((a, b) => a.label.localeCompare(b.label)).map((obj) => (
          <div key={obj.id} style={styles.objectEntry}>
            <button
              style={{ ...styles.objectBtn, background: obj.color, ...(spawning === obj.id ? { opacity: 0.5 } : {}) }}
              onClick={() => handleSpawn(obj)}
              disabled={!!spawning}
              title={`Place ${obj.label}`}
            >
              <span style={styles.objIcon}>{obj.icon}</span>
              <span style={styles.objLabel}>{obj.label}</span>
              {(obj.sizeW || obj.sizeH || obj.size > 1) && <span style={styles.objSize}>{obj.sizeW || obj.size}×{obj.sizeH || obj.size}</span>}
            </button>
            <div style={styles.hpRow}>
              <span style={styles.hpLabel}>HP</span>
              <input
                style={styles.hpInput}
                type="number" min="1"
                value={customHp[obj.id] ?? obj.hp}
                onChange={(e) => setCustomHp((p) => ({ ...p, [obj.id]: e.target.value }))}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main SpawnPanel (spawn-only, no inspector) ─────────────────────────────
const SECTIONS = ['Monsters', 'NPCs', 'Objects'];

export default function SpawnPanel({ getViewCenter }) {
  const [open, setOpen] = useState({ Monsters: true, NPCs: false, Objects: false });
  const toggle = (s) => setOpen((prev) => ({ ...prev, [s]: !prev[s] }));

  return (
    <div style={styles.panel}>
      <div style={styles.spawnSections}>
        {SECTIONS.map((s) => (
          <div key={s}>
            <SectionHeader label={s} open={open[s]} onToggle={() => toggle(s)} />
            {s === 'Monsters' && <MonstersSection open={open[s]} getViewCenter={getViewCenter} />}
            {s === 'NPCs'     && <NpcsSection     open={open[s]} getViewCenter={getViewCenter} />}
            {s === 'Objects'  && <ObjectsSection  open={open[s]} getViewCenter={getViewCenter} />}
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  panel:           { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' },
  spawnSections:   { flex: 1, overflowY: 'auto' },
  sectionHeader:   { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'transparent', border: 'none', borderBottom: '1px solid #4a3d28', color: '#d4a847', fontFamily: "'Press Start 2P', monospace", fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.07em', cursor: 'pointer' },
  sectionHeaderLabel: { display: 'flex', alignItems: 'center', gap: 8 },
  sectionBadge:    { background: '#d4943a', color: '#1a0e00', fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 3 },
  chevron:         { color: '#6a5d45', fontSize: 10 },
  section:         { padding: '12px 14px', borderBottom: '1px solid #4a3d28' },
  msg:             { background: 'rgba(74,154,90,0.12)', border: '1px solid rgba(74,154,90,0.3)', color: '#5cb870', fontSize: 12, padding: '6px 10px', borderRadius: 4, marginBottom: 8 },

  playerRow:     { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '7px 0', borderBottom: '1px solid #4a3d28' },
  playerInfo:    { display: 'flex', alignItems: 'center', gap: 8, flex: 1, overflow: 'hidden', minWidth: 0 },
  playerActions: { display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 },
  confirmRow:    { display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', minWidth: 0, flex: 1 },
  colourDot:     { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 },
  playerName:    { fontWeight: 600, fontFamily: "'Press Start 2P', monospace", fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  playerMeta:    { fontSize: 11, color: '#9a8b6e' },
  assignedBadge: { fontSize: 11, color: '#5cb870', fontWeight: 700, flexShrink: 0 },
  waitingBadge:  { fontSize: 11, color: '#d4943a', fontWeight: 700, flexShrink: 0 },
  spawnBtn:      { padding: '4px 10px', background: '#1a2a48', border: '1px solid #4a3d28', borderRadius: 4, color: '#d4a847', fontSize: 11, fontWeight: 700, cursor: 'pointer' },
  placeAllBtn:   { width: '100%', padding: '8px 0', background: '#1a2a48', border: '1px solid #4a3d28', borderRadius: 4, color: '#d4a847', fontSize: 12, fontWeight: 700, cursor: 'pointer', marginBottom: 8 },

  deleteBtn:       { background: 'transparent', border: 'none', color: '#6a5d45', cursor: 'pointer', fontSize: 14, padding: '2px 4px', borderRadius: 4, lineHeight: 1 },
  confirmText:     { fontSize: 11, color: '#e07070', flex: '1 1 100%', lineHeight: 1.3 },
  confirmYes:      { padding: '3px 8px', background: '#c0392b', border: 'none', borderRadius: 3, color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' },
  confirmNo:       { padding: '3px 8px', background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 3, color: '#9a8b6e', fontSize: 11, cursor: 'pointer' },

  emptyRosterMsg:    { padding: '12px 0', color: '#6a5d45', fontSize: 12, fontStyle: 'italic', textAlign: 'center' },
  playerNameLine:    { display: 'flex', alignItems: 'center', gap: 6 },
  noCharName:        { color: '#6a5d45', fontStyle: 'italic', fontWeight: 500 },
  onlineDot:         { width: 7, height: 7, borderRadius: '50%', flexShrink: 0 },
  orphanSection:     { marginTop: 12, paddingTop: 8, borderTop: '1px dashed #4a3d28' },
  orphanLabel:       { fontSize: 11, fontFamily: "'Press Start 2P', monospace", color: '#d4943a', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 },
  orphanLabelHint:   { fontSize: 10, color: '#6a5d45', textTransform: 'none', letterSpacing: 0, fontFamily: 'inherit', fontStyle: 'italic' },
  orphanCleanupBtn:  { alignSelf: 'flex-start', marginBottom: 6, background: 'transparent', border: '1px solid #6a4a3d', color: '#e07070', borderRadius: 4, padding: '3px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Press Start 2P', monospace" },
  orphanRow:         { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #2a2520' },

  objectGrid:    { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 },
  objectEntry:   { display: 'flex', flexDirection: 'column', gap: 3 },
  objectBtn:     { width: '100%', padding: '8px 6px', borderRadius: 4, border: '1px solid #4a3d28', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 },
  objIcon:       { fontSize: 20 },
  objLabel:      { fontSize: 10, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", color: '#f0e6d0', textTransform: 'uppercase', letterSpacing: '0.04em', textShadow: '0 1px 3px #000' },
  objSize:       { fontSize: 9, color: '#d4a847', fontWeight: 700 },
  objMeta:       { fontSize: 9, color: '#9a8b6e', fontWeight: 600 },
  hpRow:         { display: 'flex', alignItems: 'center', gap: 4 },
  hpLabel:       { fontSize: 10, color: '#6a5d45', fontWeight: 700, textTransform: 'uppercase' },
  hpInput:       { flex: 1, background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, color: '#f0e6d0', padding: '3px 6px', fontSize: 12, outline: 'none', textAlign: 'center' },

  subSectionBtn: { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', background: '#0e1528', border: 'none', borderBottom: '1px solid #4a3d28', color: '#d4943a', fontFamily: "'Press Start 2P', monospace", fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', cursor: 'pointer' },
  groupBtn:      { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 14px', background: '#0a0f1e', border: 'none', borderBottom: '1px solid #4a3d28', color: '#9a8b6e', fontFamily: "'Press Start 2P', monospace", fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', cursor: 'pointer' },
  compendiumWrap: { height: 420, display: 'flex', flexDirection: 'column', overflow: 'hidden', margin: '0 -14px', padding: '0 0' },

  packGrid:  { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 },
  packBtn:   { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: 8, background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, cursor: 'pointer' },
  packLabel: { fontSize: 10, color: '#d4a847', fontWeight: 700, textAlign: 'center', lineHeight: 1.15, wordBreak: 'break-word', overflowWrap: 'anywhere', hyphens: 'auto', width: '100%', maxWidth: '100%' },

  sizeRow:       { display: 'flex', alignItems: 'center', gap: 6, padding: '10px 14px', borderBottom: '1px solid #4a3d28', background: '#0a0f1e' },
  sizeLabel:     { fontSize: 11, fontWeight: 700, color: '#9a8b6e', textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 4 },
  sizeBtn:       { padding: '4px 10px', background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, color: '#9a8b6e', fontSize: 11, fontWeight: 700, cursor: 'pointer' },
  sizeBtnActive: { borderColor: '#d4a847', color: '#d4a847', background: '#1a2a48' },
};
